/**
 * Organisation — trois onglets : Membres (dont les demandes de rattachement en attente),
 * Licences (quota de sièges) et Domaine (revendication, vérification DNS, rattachement
 * automatique). L'onglet actif vit dans l'URL (`?onglet=`), comme `ProjectPage`.
 *
 * Mode `registre` (lot O3, `organisationStudio` de la session) : l'organisation vit au registre
 * du studio, exiPlan n'en garde qu'une projection.
 *  - membres désignés par leur **oid** (`userId` nul pour qui n'a jamais ouvert exiPlan) ;
 *  - colonne « Siège » et gestion nominative des sièges (onglet Licences, `SeatsPanel`) ;
 *  - 403 `SEAT_REQUIRED` sur les membres : écran « demander un siège » (`SeatRequiredPage`) ;
 *  - résumé `degrade: true` : bandeau et lecture seule ;
 *  - onglet Domaine et demandes de rattachement absents (lot O4, portés par le registre).
 * L'ABSENCE de `siege` dans le résumé (et le mode `local`) ne change rien à l'interface d'avant.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  orgRoleAtLeast,
  ORG_ROLES,
  ORG_ROLES_INVITABLES,
  ORG_ROLE_RANK,
} from '@exiplan/shared-types';
import { api } from '../lib/api.js';
import { messageModeleOrganisation } from '../lib/org-errors.js';
import { nomEspace } from '../lib/nom-espace.js';
import { fetchAllPages } from '../lib/pagination.js';
import { useAsync } from '../hooks/useAsync.js';
import { useSession } from '../contexts/SessionContext.jsx';
import { appAlert, appConfirm, appPrompt } from '../dialogs/service.js';
import {
  Empty,
  LoadError,
  PageBody,
  PageHeader,
  PrimaryButton,
  TruncatedListWarning,
} from '../components/common.jsx';
import { RoleBadge } from '../components/RoleBadge.jsx';
import { DnsRecords } from '../components/DnsRecords.jsx';
import { OrgInvitations, estEnAttente } from '../components/OrgInvitations.jsx';
import { LicencePanel } from '../components/LicencePanel.jsx';
import { SeatsPanel } from '../components/SeatsPanel.jsx';
import { SeatRequestBanner } from '../components/SeatRequestBanner.jsx';
import { SeatRequiredPage } from './SeatRequiredPage.jsx';
import { nomMembre, peutGererSieges } from '../lib/sieges.js';
import { MarketingConsentPanel } from '../components/MarketingConsentPanel.jsx';
import { DeleteOrganizationDialog } from '../dialogs/DeleteOrganizationDialog.jsx';
import { notifyOrganizationsChanged } from '../lib/organizationsBus.js';
import {
  BTN_DARK,
  BTN_GHOST,
  BTN_GHOST_SM,
  CELLS_PLANCHE,
  CELL_PLANCHE,
  CHECK,
  CHOIX_LABEL,
  COL_ACTIONS,
  COL_ROLE,
  COL_STATUS,
  EYEBROW,
  FORM_FIELD,
  FORM_LABEL,
  FORM_ROW,
  INPUT,
  SELECT,
  SELECT_SM,
  TAB_ACTIVE,
  TAB_IDLE,
  TAG,
  TD,
  TH,
} from '../ui/theme.js';

/** Littéraux dans l'objet — même convention que `RoleBadge::ROLE_LABEL_KEYS`. */
const STATUS_LABEL_KEYS = {
  active: 'organization.members.status.active',
  requested: 'organization.members.status.requested',
  suspended: 'organization.members.status.suspended',
};

/** Mêmes clés que `RoleBadge::ROLE_LABEL_KEYS`, dupliquées en littéral pour l'option `<select>`
 * (un `<option>` ne peut pas accueillir le composant `RoleBadge`, seulement du texte). */
const ROLE_OPTION_LABEL_KEYS = {
  proprietaire: 'roleBadge.proprietaire',
  administrateur: 'roleBadge.administrateur',
  facturation: 'roleBadge.facturation',
  editeur: 'roleBadge.editeur',
  lecteur: 'roleBadge.lecteur',
};

const TABS = ['membres', 'licences', 'domaine'];
/** Mode `registre` : le domaine est revendiqué au registre (lot O4), pas dans exiPlan. */
const TABS_REGISTRE = ['membres', 'licences'];

/** Littéraux dans l'objet — état du siège d'un membre (colonne « Siège », mode `registre`). */
const SEAT_STATE_LABEL_KEYS = {
  attribue: 'organization.members.seat.attribue',
  suspendu: 'organization.members.seat.suspendu',
};

/** Littéraux dans l'objet — le gate `keys.test.js` ne voit pas une clé de gabarit calculée. */
const TAB_LABEL_KEYS = {
  membres: 'organization.tabs.members',
  licences: 'organization.tabs.seats',
  domaine: 'organization.tabs.domain',
};

/**
 * Qui est concerné par le plan de licences.
 *
 * Le nombre de sièges n'est **pas** modifiable depuis l'application : il relève de ce que
 * l'organisation a souscrit auprès d'exiPlan (la route `PATCH /seats` n'existe pas). Ce garde
 * ne décide donc plus d'un droit d'écriture, seulement de qui se voit proposer d'augmenter son
 * plan — `proprietaire` et `facturation`, les deux rôles qui portent la relation commerciale.
 *
 * Il reste écrit en extension et non en rang : `facturation` est classé sous `administrateur` (§ 4 de la
 * spec) pour ne pas hériter de ses droits d'administration, donc `orgRoleAtLeast` l'exclurait
 * de ce qui est précisément son métier. Même forme que `organizations.service.js::canManageSeats`.
 */
function concerneParLePlan(role) {
  return role === 'proprietaire' || role === 'facturation';
}

export function OrganizationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, organisationStudio } = useSession();
  const { organizationId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [busyBase, setBusy] = useState(false);
  const [domainInput, setDomainInput] = useState('');
  const [transferTargetId, setTransferTargetId] = useState('');
  const [deletingOrg, setDeletingOrg] = useState(false);
  const inviteEmailRef = useRef(null);

  const registre = organisationStudio === 'registre';
  const tabs = registre ? TABS_REGISTRE : TABS;
  const activeTab = tabs.includes(searchParams.get('onglet'))
    ? searchParams.get('onglet')
    : 'membres';
  const setActiveTab = (tab) => {
    const next = new URLSearchParams(searchParams);
    next.set('onglet', tab);
    setSearchParams(next, { replace: true });
  };

  const {
    data: organization,
    error: organizationError,
    loading: organizationLoading,
    reload: reloadOrganization,
  } = useAsync(
    (signal) => api.get(`/organizations/${organizationId}`, { signal }),
    [organizationId],
  );
  // Mode `registre` : pendant le chargement d'une AUTRE organisation, `useAsync` garde les données
  // de la précédente — on ne les montre jamais (ni sous un autre identifiant d'URL).
  const resume = organization?.data;
  const org =
    registre && resume && String(resume.id).toLowerCase() !== String(organizationId).toLowerCase()
      ? undefined
      : resume;

  // Registre injoignable : le résumé le dit (`degrade`), l'écran passe en lecture seule. Le mode
  // `local` n'a pas cette clé et ne l'est donc jamais.
  const lectureSeule = registre && org?.degrade === true;
  const busy = busyBase || lectureSeule;

  /** Identifiant d'un membre dans les routes : oid en mode `registre`, userId en mode `local`. */
  const memberKey = (member) => (registre ? member.oid : member.userId);

  const canManage = orgRoleAtLeast(org?.role, 'administrateur');
  /**
   * Colonne « Siège » : le siège nominatif est un détail réservé aux rôles de gestion (§ 4.2 de la
   * spec licences, issue #173) ; `editeur`/`lecteur` reçoivent les noms seuls, sans `siege`.
   */
  const colonneSiege = registre && peutGererSieges(org?.role);
  const estPersonnel = org?.kind === 'personnel';
  /**
   * Seul un `owner` peut attribuer ou retirer le rôle `owner` (miroir du garde serveur dans
   * `organizations.service.js::updateOrgMember`) : un admin ne doit même pas voir l'option dans
   * le sélecteur, sans quoi il obtient un 403 opaque en la choisissant — et surtout, l'absence
   * de l'option côté serveur ne doit jamais être la seule barrière.
   */
  const assignableRoles =
    org?.role === 'proprietaire' ? ORG_ROLES : ORG_ROLES.filter((role) => role !== 'proprietaire');
  /**
   * Rôles qu'une invitation peut accorder. Mode `registre` : jamais `proprietaire` (ACC-4) et
   * STRICTEMENT inférieurs au rôle de l'émetteur (miroir de
   * `invitations.service.js::assertRoleGrantable`). Mode `local` : comportement d'avant le lot O3,
   * soit les mêmes rôles que le sélecteur de rôle d'un membre.
   */
  const invitableRoles =
    organisationStudio === 'registre'
      ? ORG_ROLES_INVITABLES.filter(
          (role) => ORG_ROLE_RANK[role] > (ORG_ROLE_RANK[org?.role] ?? Infinity),
        )
      : assignableRoles;

  const {
    data: membersData,
    error: membersError,
    loading: membersLoading,
    reload: reloadMembers,
  } = useAsync(
    (signal) => fetchAllPages(`/organizations/${organizationId}/members`, { signal }),
    [organizationId],
  );
  const members = membersData?.data ?? [];

  const {
    data: joinRequestsData,
    error: joinRequestsError,
    reload: reloadJoinRequests,
  } = useAsync(
    (signal) =>
      canManage && !registre
        ? fetchAllPages(`/organizations/${organizationId}/join-requests`, { signal })
        : Promise.resolve({ data: [] }),
    [organizationId, canManage, registre],
  );
  const joinRequests = joinRequestsData?.data ?? [];

  // Compte de la rangée d'indicateurs (217:4531) : combien d'invitations sont EN ATTENTE, pas
  // combien l'historique en contient — même filtre que `OrgInvitations::estEnAttente`, seule la
  // liste elle-même vit dans ce composant-là. Mode `registre` : le registre n'expose pas cette
  // liste (exiAdmin#85), l'indicateur n'y est pas affiché et rien n'est demandé.
  const { data: invitationsData } = useAsync(
    (signal) =>
      canManage && !registre
        ? fetchAllPages(`/organizations/${organizationId}/invitations`, { signal })
        : Promise.resolve({ data: [] }),
    [organizationId, canManage, registre],
  );
  const pendingInvitationsCount = (invitationsData?.data ?? []).filter(estEnAttente).length;

  /*
   * Le jeton de vérification est retiré de `GET /organizations/:id` (il n'a d'utilité que pour
   * l'admin qui configure le DNS) : il faut donc le lire sur sa route dédiée, réservée à
   * `admin`. Sans cet appel, l'écran affichait un enregistrement DNS à la valeur vide.
   */
  const {
    data: domainData,
    error: domainError,
    reload: reloadDomain,
  } = useAsync(
    (signal) =>
      canManage && !registre
        ? api.get(`/organizations/${organizationId}/domain`, { signal })
        : Promise.resolve({ data: null }),
    [organizationId, canManage, registre],
  );
  const domainInfo = domainData?.data;

  const {
    data: seatsData,
    error: seatsError,
    reload: reloadSeats,
  } = useAsync(
    (signal) => api.get(`/organizations/${organizationId}/seats`, { signal }),
    [organizationId],
  );
  const seats = seatsData?.data;

  /**
   * Mode `registre` : un 403 `SEAT_REQUIRED` sur les membres dit que l'appelant n'a pas de siège
   * (jamais d'auto-siège possible : plus de place, rôle `facturation`, siège suspendu…). C'est un
   * ÉTAT de l'écran, pas une panne : il remplace la page par « demander un siège ».
   */
  /**
   * Trois familles de rôles sans siège (résumé `siege: false`) :
   *  - `facturation` : jamais d'auto-siège (AUTO-4) — le résumé fait foi, écran « pas de siège »
   *    (avec la gestion des sièges, dont l'attribution à un tiers) ;
   *  - `proprietaire`/`administrateur` : ils ont droit à l'auto-siège, mais `GET /members` leur est
   *    exempté et n'en tente aucun (#172) — on tente donc l'auto-siège par un accès GARDÉ (la
   *    sonde, `GET …/invitations`). Si elle échoue (organisation pleine, siège suspendu…), la page
   *    de gestion RESTE (les gestes que le serveur laisse sans siège : membres, transfert, sièges,
   *    licence) avec un bandeau ; jamais d'enfermement sur l'écran « pas de siège » ;
   *  - `editeur`/`lecteur` : l'accès aux membres (gardé) tente l'auto-siège, sinon 403
   *    `SEAT_REQUIRED` → écran « pas de siège ».
   */
  const role = org?.role;
  const sondable = role === 'proprietaire' || role === 'administrateur';
  const aSonder = registre && org?.siege === false && sondable;
  const { data: sondeData, error: sondeError } = useAsync(
    (signal) =>
      aSonder
        ? api.get(`/organizations/${organizationId}/invitations?page=1&pageSize=1`, { signal })
        : Promise.resolve(null),
    [organizationId, aSonder],
  );
  const siegeRequis =
    registre &&
    ((membersError?.isSeatRequired === true && !sondable) ||
      (org?.siege === false && role === 'facturation'));
  const bandeauSansSiege = aSonder && Boolean(sondeError);

  /**
   * Le résumé ne tente jamais l'auto-siège : seul un accès à une collection GARDÉE le fait. Quand
   * cet accès a réussi (sonde, ou membres pour un éditeur/lecteur) alors que le résumé disait
   * `siege: false`, le siège vient d'être attribué — on relit le résumé UNE fois (silencieusement)
   * pour qu'il ne reste pas périmé. Le garde est réarmé à chaque changement d'organisation.
   */
  const resumePerimeRelu = useRef(false);
  useEffect(() => {
    resumePerimeRelu.current = false;
  }, [organizationId]);
  useEffect(() => {
    if (!registre || org?.siege !== false || resumePerimeRelu.current) return;
    const autoSiegeAccepte = sondable
      ? Boolean(sondeData)
      : role !== 'facturation' && Boolean(membersData) && !membersError;
    if (autoSiegeAccepte) {
      resumePerimeRelu.current = true;
      reloadOrganization();
    }
    // `organization` : un nouveau résumé relance l'évaluation — c'est le garde ci-dessus qui
    // empêche la boucle de relectures.
  }, [
    registre,
    org?.siege,
    sondable,
    role,
    sondeData,
    membersData,
    membersError,
    organization,
    reloadOrganization,
  ]);

  // Demandes, sièges et domaine : un échec n'empêche pas l'écran, mais ne doit pas laisser des
  // compteurs à zéro sans rien dire. On ne relance que ce qui a échoué. Le refus de siège des
  // sièges (même cause que celui des membres) n'est pas une panne à signaler.
  const seatsErreurAffichee = seatsError?.isSeatRequired ? null : seatsError;
  const secondaryError = joinRequestsError ?? seatsErreurAffichee ?? domainError;
  const retrySecondary = () => {
    if (joinRequestsError) reloadJoinRequests();
    if (seatsError) reloadSeats();
    if (domainError) reloadDomain();
  };

  // Mode `registre` : la relecture du résumé (auto-siège) est silencieuse, la page reste affichée.
  if ((organizationLoading && !(registre && org)) || (registre && !org && !organizationError)) {
    return (
      <div>
        <PageHeader eyebrow={t('organization.eyebrow')} title={t('organization.title')} />
        <p className="p-4 text-sm text-ink-soft">{t('organization.loading')}</p>
      </div>
    );
  }

  if (organizationError) {
    const notFound = organizationError.status === 404;
    return (
      <div>
        <PageHeader eyebrow={t('organization.eyebrow')} title={t('organization.notFoundTitle')} />
        <PageBody className="p-4">
          <Empty>
            {notFound
              ? t('organization.notFound')
              : t('organization.loadError', { message: organizationError.message })}
          </Empty>
        </PageBody>
      </div>
    );
  }

  if (siegeRequis) {
    // `facturation` sans siège (jamais auto-servi) garde la main sur les sièges : `GET /seats`,
    // `GET /members` et les gestes de siège lui restent ouverts côté serveur (exemption des rôles
    // de gestion, #172) — il peut donc attribuer à un tiers.
    return (
      <SeatRequiredPage organization={org} degrade={lectureSeule}>
        {peutGererSieges(org.role) && seats?.sieges && (
          <div className={`${CELLS_PLANCHE} grid-cols-1`}>
            <SeatsPanel
              organizationId={organizationId}
              seats={seats}
              members={members}
              attribution={!membersError}
              currentRole={org.role}
              currentUserId={user?.id}
              lectureSeule={lectureSeule}
              onChanged={() => {
                reloadSeats();
                reloadMembers();
                reloadOrganization();
              }}
            />
          </div>
        )}
      </SeatRequiredPage>
    );
  }

  // Refus du modèle d'organisation (ORG-4, e-mail non vérifié…) : message traduit plutôt que le
  // texte serveur, toujours en français (#121).
  // Cette page est celle de l'ADMIN : ALREADY_IN_ORGANIZATION s'y adresse à lui, pas à l'invité.
  const messageErreur = (error) =>
    error?.details?.code === 'ALREADY_IN_ORGANIZATION'
      ? t('orgErrors.alreadyInOrganizationAdmin')
      : (messageModeleOrganisation(error, t) ?? error.message);

  const changeRole = async (userId, role) => {
    setBusy(true);
    try {
      await api.patch(`/organizations/${organizationId}/members/${userId}`, { role });
      await reloadMembers();
    } catch (error) {
      await appAlert(
        t('organization.members.roleChangeFailed', { message: messageErreur(error) }),
        {
          title: t('organization.members.roleChangeTitle'),
        },
      );
      await reloadMembers();
    } finally {
      setBusy(false);
    }
  };

  const changeStatus = async (userId, status) => {
    setBusy(true);
    try {
      await api.patch(`/organizations/${organizationId}/members/${userId}`, { status });
      await reloadMembers();
      await reloadSeats();
    } catch (error) {
      await appAlert(
        t('organization.members.statusChangeFailed', { message: messageErreur(error) }),
        {
          title: t('organization.members.statusChangeTitle'),
        },
      );
    } finally {
      setBusy(false);
    }
  };

  const removeMember = async (member) => {
    if (
      !(await appConfirm(
        t('organization.members.removeConfirm', {
          name: nomMembre(member) ?? t('organization.members.unnamed'),
        }),
        { confirmLabel: t('organization.members.removeConfirmLabel') },
      ))
    )
      return;
    setBusy(true);
    try {
      await api.delete(`/organizations/${organizationId}/members/${memberKey(member)}`);
      await reloadMembers();
      await reloadSeats();
    } catch (error) {
      await appAlert(t('organization.members.removeFailed', { message: messageErreur(error) }), {
        title: t('organization.members.removeFailedTitle'),
      });
    } finally {
      setBusy(false);
    }
  };

  const approveJoinRequest = async (userId) => {
    setBusy(true);
    try {
      await api.post(`/organizations/${organizationId}/join-requests/${userId}/approve`);
      await reloadJoinRequests();
      await reloadMembers();
      await reloadSeats();
    } catch (error) {
      await appAlert(t('organization.members.approveFailed', { message: messageErreur(error) }), {
        title: t('organization.members.approveFailedTitle'),
      });
    } finally {
      setBusy(false);
    }
  };

  const rejectJoinRequest = async (userId) => {
    setBusy(true);
    try {
      await api.delete(`/organizations/${organizationId}/join-requests/${userId}`);
      await reloadJoinRequests();
    } catch (error) {
      await appAlert(t('organization.members.rejectFailed', { message: error.message }), {
        title: t('organization.members.rejectFailedTitle'),
      });
    } finally {
      setBusy(false);
    }
  };

  const claimDomain = async (event) => {
    event.preventDefault();
    if (!domainInput.trim()) return;
    setBusy(true);
    try {
      await api.post(`/organizations/${organizationId}/domain`, { domain: domainInput.trim() });
      setDomainInput('');
      await reloadOrganization();
      await reloadDomain();
    } catch (error) {
      await appAlert(t('organization.domain.claimFailed', { message: error.message }), {
        title: t('organization.domain.claimFailedTitle'),
      });
    } finally {
      setBusy(false);
    }
  };

  const verifyDomain = async () => {
    setBusy(true);
    try {
      await api.post(`/organizations/${organizationId}/domain/verify`);
      await reloadOrganization();
      await reloadDomain();
    } catch (error) {
      await appAlert(t('organization.domain.verifyFailed', { message: error.message }), {
        title: t('organization.domain.verifyFailedTitle'),
      });
    } finally {
      setBusy(false);
    }
  };

  const toggleAutoJoin = async () => {
    setBusy(true);
    try {
      await api.patch(`/organizations/${organizationId}/domain`, {
        autoJoinVerifiedDomain: !org.autoJoinVerifiedDomain,
      });
      await reloadOrganization();
      await reloadDomain();
    } catch (error) {
      await appAlert(t('organization.domain.autoJoinChangeFailed', { message: error.message }), {
        title: t('organization.domain.autoJoinChangeFailedTitle'),
      });
    } finally {
      setBusy(false);
    }
  };

  const renameOrganization = async () => {
    const name = await appPrompt(t('organization.renamePromptMessage'), org.name, {
      title: t('organization.renamePromptTitle'),
    });
    if (!name || !name.trim() || name.trim() === org.name) return;
    setBusy(true);
    try {
      await api.patch(`/organizations/${organizationId}`, { name: name.trim() });
      await reloadOrganization();
    } catch (error) {
      await appAlert(t('organization.renameFailed', { message: error.message }), {
        title: t('organization.renamePromptTitle'),
      });
    } finally {
      setBusy(false);
    }
  };

  const leaveOrganization = async () => {
    if (
      !(await appConfirm(t('organization.leaveConfirm'), {
        confirmLabel: t('organization.leaveConfirmLabel'),
      }))
    )
      return;
    setBusy(true);
    try {
      await api.delete(`/organizations/${organizationId}/members/me`);
      navigate('/compte?onglet=profil');
    } catch (error) {
      // Refus attendu et légitime : dernier propriétaire actif. Le message serveur dit déjà de
      // transférer la propriété d'abord — il s'affiche tel quel, sans reformulation.
      await appAlert(messageModeleOrganisation(error, t) ?? error.message, {
        title: t('organization.leaveFailedTitle'),
      });
    } finally {
      setBusy(false);
    }
  };

  const transferOwnership = async () => {
    if (!transferTargetId) return;
    const target = members.find((member) => memberKey(member) === transferTargetId);
    if (
      !(await appConfirm(
        t('organization.transferConfirm', {
          name: nomMembre(target ?? {}) ?? t('organization.members.unnamed'),
        }),
        { confirmLabel: t('organization.transferConfirmLabel') },
      ))
    )
      return;
    setBusy(true);
    try {
      await api.post(
        `/organizations/${organizationId}/transfer-ownership`,
        // Mode `registre` : le membre est désigné par son oid (il peut n'avoir aucun compte).
        registre ? { oid: transferTargetId } : { userId: transferTargetId },
      );
      setTransferTargetId('');
      await reloadOrganization();
      await reloadMembers();
    } catch (error) {
      await appAlert(t('organization.transferFailed', { message: messageErreur(error) }), {
        title: t('organization.transferFailedTitle'),
      });
    } finally {
      setBusy(false);
    }
  };

  /**
   * Le rafraîchissement du lien « Organisation » de l'`AppShell` et la redirection sont propres
   * à cette page (§1.4 de la spec) : le dialogue lui-même ne fait que remonter le succès, il ne
   * sait rien de l'arbre au-dessus de lui.
   */
  const onOrganizationDeleted = () => {
    setDeletingOrg(false);
    notifyOrganizationsChanged();
    navigate('/compte?onglet=profil');
  };

  /*
   * Les propriétaires sont déduits de la page de membres déjà chargée — aucun appel de plus.
   * Limite assumée : `listOrgMembers` pagine à 50 en `createdAt ASC`. Le propriétaire fondateur
   * est le plus ancien membre, donc toujours en première page ; seule une propriété transférée
   * à un membre récent, dans une organisation de plus de 50 personnes, pourrait échapper à cette
   * lecture. Une adhésion suspendue ne gouverne plus rien : elle est exclue.
   */
  const owners = members
    .filter((member) => member.role === 'proprietaire' && member.status === 'active')
    .map((member) => nomMembre(member) ?? t('organization.members.unnamed'));

  /*
   * LE primaire de l'écran (règle de cohérence 1.3.0 d'`@exidis/ui`) dépend de l'onglet, à
   * droite de « Renommer » : « Inviter un membre » sur Membres (il amène au formulaire
   * d'invitation, qui garde son « Inviter » fantôme), « Augmenter le plan » sur Licences (plus
   * de doublon dans la cellule). Domaine n'a pas d'action principale : pas de primaire inventé.
   */
  const focusInvitation = () => {
    const field = inviteEmailRef.current;
    if (!field) return;
    field.scrollIntoView?.({ block: 'center' });
    field.focus();
  };
  let primaryAction = null;
  if (activeTab === 'membres' && canManage) {
    primaryAction = (
      <PrimaryButton disabled={lectureSeule} onClick={focusInvitation}>
        {t('organization.inviteMember')}
      </PrimaryButton>
    );
  } else if (activeTab === 'licences' && concerneParLePlan(org.role)) {
    primaryAction = (
      <PrimaryButton disabled aria-describedby="plan-bientot">
        {t('organization.seats.upgradePlan')}
      </PrimaryButton>
    );
  }

  return (
    <div>
      <PageHeader
        eyebrow={t('organization.eyebrow')}
        title={nomEspace(org, t)}
        subtitle={
          owners.length > 0
            ? t('organization.ownedBy', { count: owners.length, names: owners.join(', ') })
            : undefined
        }
        actions={
          // ORG-8 : pas de « Renommer » sur l'espace personnel, dont le nom est fixe.
          canManage &&
          !estPersonnel && (
            <button
              type="button"
              className={BTN_GHOST}
              disabled={busy}
              onClick={renameOrganization}
            >
              {t('organization.rename')}
            </button>
          )
        }
        primary={primaryAction}
      />

      {/* Trait 2 px sur l'enveloppe non bornée, onglets bornés comme le corps. */}
      <div className="border-b-2 border-line">
        <nav
          className="mx-auto flex w-full max-w-(--largeur-corps) items-end gap-1 px-4 pt-2"
          aria-label={t('organization.tabsAriaLabel')}
        >
          {tabs.map((tab) => (
            <button
              key={tab}
              type="button"
              className={activeTab === tab ? TAB_ACTIVE : TAB_IDLE}
              aria-current={activeTab === tab ? 'page' : undefined}
              onClick={() => setActiveTab(tab)}
            >
              {t(TAB_LABEL_KEYS[tab])}
            </button>
          ))}
        </nav>
      </div>

      <PageBody className="p-4">
        {bandeauSansSiege && <SeatRequestBanner organization={org} degrade={lectureSeule} />}
        {lectureSeule && (
          <div
            role="status"
            className="mb-4 border-2 border-accent-trait bg-accent-wash px-3 py-2 text-sm text-accent-ink"
          >
            {t('organization.readOnlyBanner')}
          </div>
        )}
        {secondaryError && (
          <LoadError error={secondaryError} onRetry={retrySecondary} className="mb-4" />
        )}
        {activeTab === 'membres' && (
          <div className="flex flex-col gap-4">
            {/* Rangée d'indicateurs (217:4531) : membres, demandes de rattachement, invitations
                en attente, sièges — un coup d'œil sur l'état de l'organisation avant le détail
                des tableaux. Mode `registre` : ni demandes de rattachement ni liste d'invitations
                (le registre n'en expose pas) — membres et sièges seulement. */}
            <div
              data-testid="organization-indicators"
              className={`${CELLS_PLANCHE} grid-cols-2 ${registre ? 'sm:grid-cols-2' : 'sm:grid-cols-4'}`}
            >
              <Indicateur
                libelle={t('organization.indicators.members')}
                valeur={members.filter((member) => member.status === 'active').length}
              />
              {!registre && (
                <Indicateur
                  libelle={t('organization.indicators.joinRequests')}
                  valeur={joinRequests.length}
                />
              )}
              {!registre && (
                <Indicateur
                  libelle={t('organization.indicators.invitations')}
                  valeur={pendingInvitationsCount}
                />
              )}
              <Indicateur
                libelle={t('organization.indicators.seats')}
                valeur={seats ? `${seats.used}/${seats.limit ?? '∞'}` : '—'}
              />
            </div>

            {membersError && <LoadError error={membersError} onRetry={reloadMembers} />}
            {membersData?.truncated && <TruncatedListWarning />}

            <div className={`${CELLS_PLANCHE} grid-cols-1`}>
              <div className={`${CELL_PLANCHE} bg-paper p-6`}>
                <span className="ticks" aria-hidden="true" />
                {membersLoading ? (
                  <p className="text-sm text-ink-soft">{t('organization.loading')}</p>
                ) : membersError ? null : members.length === 0 ? (
                  <Empty>{t('organization.members.empty')}</Empty>
                ) : (
                  <table className="w-full table-fixed border-collapse">
                    {/* Grille de colonnes partagée par les tableaux de l'organisation
                      (217:4531 : « [FILL | 210 | 150 | 230] ») — Rôle à largeur fixe et
                      alignée : sans elle, la colonne Rôle (sélecteur natif ou pastille) sautait
                      d'une largeur à l'autre selon la ligne, et le tableau débordait à droite. */}
                    <colgroup>
                      <col className="w-auto" />
                      <col className={COL_ROLE} />
                      <col className={COL_STATUS} />
                      {colonneSiege && <col className={COL_STATUS} />}
                      <col className={COL_ACTIONS} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th className={TH}>{t('organization.members.columnMember')}</th>
                        <th className={TH}>{t('organization.members.columnRole')}</th>
                        <th className={TH}>{t('organization.members.columnStatus')}</th>
                        {colonneSiege && (
                          <th className={TH}>{t('organization.members.columnSeat')}</th>
                        )}
                        <th className={TH}>{t('organization.members.columnActions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {members.map((member) => (
                        <tr key={memberKey(member)}>
                          <td className={TD}>
                            <span className="block text-ink">
                              {nomMembre(member) ?? t('organization.members.unnamed')}
                            </span>
                            {member.name && member.email && (
                              <span className="block font-mono text-[11px] text-ink-faint">
                                {member.email}
                              </span>
                            )}
                            {registre && !member.userId && (
                              <span className="block text-[11px] text-ink-faint">
                                {t('organization.members.noAccount')}
                              </span>
                            )}
                          </td>
                          <td className={TD}>
                            {canManage &&
                            (org.role === 'proprietaire' || member.role !== 'proprietaire') ? (
                              <select
                                aria-label={t('organization.members.roleAria', {
                                  name: nomMembre(member) ?? t('organization.members.unnamed'),
                                })}
                                className={SELECT_SM}
                                value={member.role}
                                onChange={(event) =>
                                  changeRole(memberKey(member), event.target.value)
                                }
                                disabled={busy}
                              >
                                {assignableRoles.map((role) => (
                                  <option key={role} value={role}>
                                    {t(ROLE_OPTION_LABEL_KEYS[role])}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <RoleBadge role={member.role} />
                            )}
                          </td>
                          <td className={TD}>
                            <span className={`${TAG} border-line text-ink-soft`}>
                              {STATUS_LABEL_KEYS[member.status]
                                ? t(STATUS_LABEL_KEYS[member.status])
                                : member.status}
                            </span>
                          </td>
                          {colonneSiege && (
                            <td className={TD}>
                              <span className={`${TAG} border-line text-ink-soft`}>
                                {t(
                                  SEAT_STATE_LABEL_KEYS[member.siege] ??
                                    'organization.members.seat.aucun',
                                )}
                              </span>
                            </td>
                          )}
                          <td className={TD}>
                            {canManage && (
                              <div className="flex flex-wrap gap-2">
                                {(member.status === 'active' || member.status === 'suspended') && (
                                  <button
                                    type="button"
                                    className={BTN_GHOST_SM}
                                    disabled={busy}
                                    onClick={() =>
                                      changeStatus(
                                        memberKey(member),
                                        member.status === 'active' ? 'suspended' : 'active',
                                      )
                                    }
                                  >
                                    {member.status === 'active'
                                      ? t('organization.members.suspend')
                                      : t('organization.members.reactivate')}
                                  </button>
                                )}
                                <button
                                  type="button"
                                  className={BTN_GHOST_SM}
                                  disabled={busy}
                                  onClick={() => removeMember(member)}
                                >
                                  {t('organization.members.remove')}
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {canManage && (
                  <div className="mt-4">
                    <OrgInvitations
                      scope="organization"
                      scopeId={organizationId}
                      assignableRoles={invitableRoles}
                      emailInputRef={inviteEmailRef}
                      registre={registre}
                      lectureSeule={lectureSeule}
                    />
                  </div>
                )}

                {canManage && !registre && (
                  <section className="mt-4 border-t-2 border-line pt-4">
                    <p className="font-mono text-[0.68rem] uppercase tracking-eyebrow text-ink-faint">
                      {t('organization.members.joinRequestsTitle')}
                    </p>
                    {joinRequests.length === 0 ? (
                      <p className="mt-2 text-sm text-ink-soft">
                        {t('organization.members.joinRequestsEmpty')}
                      </p>
                    ) : (
                      <ul className="mt-2 flex flex-col gap-2">
                        {joinRequests.map((request) => (
                          <li
                            key={request.userId}
                            className="flex items-center justify-between gap-2 border-b-2 border-line-soft py-2"
                          >
                            <span className="text-sm text-ink">
                              {request.name || request.email}
                            </span>
                            <div className="flex gap-2">
                              <button
                                type="button"
                                className={BTN_GHOST_SM}
                                disabled={busy}
                                onClick={() => approveJoinRequest(request.userId)}
                              >
                                {t('organization.members.approve')}
                              </button>
                              <button
                                type="button"
                                className={BTN_GHOST_SM}
                                disabled={busy}
                                onClick={() => rejectJoinRequest(request.userId)}
                              >
                                {t('organization.members.reject')}
                              </button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>
                )}
              </div>
            </div>

            {/* Consentement à la communication du studio : porté par le registre, propre à la
                personne propriétaire (c'est son adresse) — lui seul le voit et le modifie. */}
            {registre && org.role === 'proprietaire' && (
              <MarketingConsentPanel organizationId={organizationId} />
            )}

            {/* Adhésion et Zone de danger côte à côte (217:4531) — deux cellules de la même
                planche (planche B : gouttière de 16 px du paquet), plutôt qu'empilées comme le
                reste du corps de page. */}
            <div className={`${CELLS_PLANCHE} grid-cols-1 sm:grid-cols-2`}>
              <div className={`${CELL_PLANCHE} bg-paper p-6`}>
                <span className="ticks" aria-hidden="true" />
                <section className="flex flex-col items-start gap-3">
                  <p className="font-mono text-[0.68rem] tracking-eyebrow text-ink-faint uppercase">
                    {t('organization.membership.title')}
                  </p>

                  {org.role === 'proprietaire' && (
                    <div className={FORM_ROW}>
                      <label className={FORM_FIELD}>
                        <span className={FORM_LABEL}>
                          {t('organization.membership.transferLabel')}
                        </span>
                        <select
                          className={SELECT}
                          value={transferTargetId}
                          onChange={(event) => setTransferTargetId(event.target.value)}
                          disabled={busy}
                        >
                          <option value="">
                            {t('organization.membership.transferPlaceholder')}
                          </option>
                          {members
                            .filter(
                              (member) => member.userId !== user?.id && member.status === 'active',
                            )
                            .map((member) => (
                              <option key={memberKey(member)} value={memberKey(member)}>
                                {nomMembre(member) ?? t('organization.members.unnamed')}
                              </option>
                            ))}
                        </select>
                      </label>
                      <button
                        type="button"
                        className={BTN_GHOST}
                        disabled={busy || !transferTargetId}
                        onClick={transferOwnership}
                      >
                        {t('organization.membership.transfer')}
                      </button>
                    </div>
                  )}
                  <p className="text-xs text-ink-faint">
                    {t('organization.membership.transferWarning')}
                  </p>

                  <button
                    type="button"
                    className={BTN_GHOST_SM}
                    disabled={busy}
                    onClick={leaveOrganization}
                  >
                    {t('organization.membership.leave')}
                  </button>
                </section>
              </div>

              {org.role === 'proprietaire' && (
                <div className={`${CELL_PLANCHE} bg-paper p-6`}>
                  <span className="ticks" aria-hidden="true" />
                  <section className="flex flex-col items-start gap-3">
                    <p className="font-mono text-[0.68rem] tracking-eyebrow text-ink-faint uppercase">
                      {t('organization.danger.title')}
                    </p>
                    <button
                      type="button"
                      className={BTN_DARK}
                      disabled={busy}
                      onClick={() => setDeletingOrg(true)}
                    >
                      {t('organization.danger.deleteButton')}
                    </button>
                  </section>
                </div>
              )}
            </div>
          </div>
        )}

        {deletingOrg && (
          <DeleteOrganizationDialog
            organizationId={organizationId}
            organizationName={org.name}
            onClose={() => setDeletingOrg(false)}
            onDeleted={onOrganizationDeleted}
          />
        )}

        {activeTab === 'licences' && seats && (
          <div className={`${CELLS_PLANCHE} grid-cols-1`}>
            <div className={`${CELL_PLANCHE} flex flex-col gap-4 bg-paper p-6`}>
              <span className="ticks" aria-hidden="true" />
              {seats.limit !== null && seats.used > seats.limit && (
                <div
                  role="status"
                  className="border-2 border-accent-trait bg-accent-wash px-3 py-2 text-sm text-accent-ink"
                >
                  {t('organization.seats.overLimitBanner')}
                </div>
              )}

              <p className="text-sm text-ink">
                {t('organization.seats.usage', { used: seats.used, limit: seats.limit ?? '∞' })}
              </p>

              <p className="text-xs text-ink-faint">{t('organization.seats.managedByExiplan')}</p>

              {/* « Augmenter le plan » est le primaire de l'en-tête sur cet onglet ; seule son
                  explication reste ici, référencée par `aria-describedby`. */}
              {concerneParLePlan(org.role) && (
                <p id="plan-bientot" className="text-xs text-ink-faint">
                  {t('organization.seats.upgradeSoon')}
                </p>
              )}

              <LicencePanel
                organizationId={organizationId}
                licence={seats.licence}
                canManage={concerneParLePlan(org.role)}
                onChanged={reloadSeats}
              />
            </div>
            {/* Mode `registre` : liste nominative des sièges et gestes — servie aux seuls rôles
                de gestion (`seats.sieges` absent sinon, le composant ne rend alors rien). */}
            {registre && peutGererSieges(org.role) && (
              <SeatsPanel
                organizationId={organizationId}
                seats={seats}
                members={members}
                currentUserId={user?.id}
                lectureSeule={lectureSeule}
                currentRole={org.role}
                onChanged={() => {
                  reloadSeats();
                  reloadMembers();
                  // Une attribution à soi-même change le résumé (`siege`) : le bandeau doit tomber.
                  reloadOrganization();
                }}
              />
            )}
          </div>
        )}

        {activeTab === 'domaine' && (
          <div className={`${CELLS_PLANCHE} max-w-3xl grid-cols-1`}>
            {/*
              Trois cellules empilées, dans l'ordre où l'on s'en sert : ce que porte
              l'organisation aujourd'hui, ce que le domaine vérifié autorise, puis seulement
              le changement. Auparavant le domaine déclaré était en haut et le formulaire qui
              le REMPLACE tout en bas sous « Nouveau domaine », sans dire qu'il écrasait
              l'existant ni qu'il remettait la vérification à zéro.
            */}
            {org.primaryDomain ? (
              <>
                <div className={`${CELL_PLANCHE} bg-paper p-6`}>
                  <span className="ticks" aria-hidden="true" />
                  <section className="flex flex-col items-start gap-3">
                    <p className="font-mono text-[0.68rem] tracking-eyebrow text-ink-faint uppercase">
                      {t('organization.domain.sectionCurrent')}
                    </p>
                    <p className="text-sm text-ink">
                      {t('organization.domain.claimedLabel')} : <strong>{org.primaryDomain}</strong>
                    </p>
                    <span
                      className={`${TAG} ${org.domainVerifiedAt ? 'border-accent-trait bg-accent-wash text-accent-ink' : 'border-line text-ink-soft'}`}
                    >
                      {org.domainVerifiedAt
                        ? t('organization.domain.verified')
                        : t('organization.domain.notVerified')}
                    </span>

                    {!org.domainVerifiedAt && domainInfo?.domainVerificationToken && (
                      <DnsRecords
                        domain={org.primaryDomain}
                        token={domainInfo.domainVerificationToken}
                      />
                    )}

                    {canManage && !org.domainVerifiedAt && (
                      <button
                        type="button"
                        className={BTN_GHOST}
                        disabled={busy}
                        onClick={verifyDomain}
                      >
                        {t('organization.domain.verify')}
                      </button>
                    )}
                  </section>
                </div>

                <div className={`${CELL_PLANCHE} bg-paper p-6`}>
                  <span className="ticks" aria-hidden="true" />
                  <section className="flex flex-col gap-2">
                    <p className="font-mono text-[0.68rem] tracking-eyebrow text-ink-faint uppercase">
                      {t('organization.domain.sectionAutoJoin')}
                    </p>
                    <label className={CHOIX_LABEL}>
                      <input
                        className={CHECK}
                        type="checkbox"
                        checked={org.autoJoinVerifiedDomain}
                        disabled={!canManage || !org.domainVerifiedAt}
                        onChange={toggleAutoJoin}
                      />
                      {t('organization.domain.autoJoinLabel')}
                    </label>
                    {!org.domainVerifiedAt && (
                      <p className="text-xs text-ink-faint">
                        {t('organization.domain.autoJoinDisabledReason')}
                      </p>
                    )}
                  </section>
                </div>
              </>
            ) : (
              <div className={`${CELL_PLANCHE} bg-paper p-6`}>
                <span className="ticks" aria-hidden="true" />
                <Empty>{t('organization.domain.none')}</Empty>
              </div>
            )}

            {canManage && (
              <div className={`${CELL_PLANCHE} bg-paper p-6`}>
                <span className="ticks" aria-hidden="true" />
                <section className="flex flex-col gap-2">
                  <p className="font-mono text-[0.68rem] tracking-eyebrow text-ink-faint uppercase">
                    {org.primaryDomain
                      ? t('organization.domain.sectionChange')
                      : t('organization.domain.sectionClaim')}
                  </p>
                  {org.primaryDomain ? (
                    <p role="note" className="text-sm text-ink">
                      {t('organization.domain.changeWarning', { domain: org.primaryDomain })}
                    </p>
                  ) : (
                    <p className="text-sm text-ink-soft">{t('organization.domain.claimIntro')}</p>
                  )}
                  <form onSubmit={claimDomain} className={FORM_ROW}>
                    <label className={FORM_FIELD}>
                      <span className={FORM_LABEL}>
                        {org.primaryDomain
                          ? t('organization.domain.newDomainLabel')
                          : t('organization.domain.claimLabel')}
                      </span>
                      <input
                        aria-label={t('organization.domain.claimAria')}
                        className={INPUT}
                        placeholder={t('organization.domain.claimPlaceholder')}
                        value={domainInput}
                        onChange={(event) => setDomainInput(event.target.value)}
                      />
                    </label>
                    <button type="submit" className={BTN_GHOST} disabled={busy}>
                      {org.primaryDomain
                        ? t('organization.domain.change')
                        : t('organization.domain.claim')}
                    </button>
                  </form>
                </section>
              </div>
            )}
          </div>
        )}
      </PageBody>
    </div>
  );
}

/** Cellule d'indicateur — libellé en eyebrow, valeur en grand (217:4531). */
function Indicateur({ libelle, valeur }) {
  return (
    <div className={`${CELL_PLANCHE} bg-paper p-4`}>
      <span className="ticks" aria-hidden="true" />
      <p className={EYEBROW}>{libelle}</p>
      <p className="mt-1 font-mono text-2xl tabular-nums text-ink">{valeur}</p>
    </div>
  );
}
